import React from 'react';
import { withProps } from 'recompose';

import Metadata from 'components/Metadata/Metadata';

import {
  ArtworkWrapper,
  EntityLink,
  Entity,
  StyledArtwork,
  EntityMetadata,
  TitleWrapper,
  EntityTitle,
  ReleaseTypeWrapper,
  Tag,
} from './EntityItem.style';

import getEntityUrl from 'url-constructors/getEntityUrl';
import { apiTypeToEntityType } from 'helpers/mapEntityTypes';
import DoubleMetricCell from 'components/data-visualization/Table/DoubleMetricCell';
import { EntityTypes } from 'context/entityContext';

interface IEntityItemExtProps {
  /** The ID of this entity */
  entityId: string;
  /** The optional ID of the parent entity */
  parentEntityId?: string;
  /** The type of this entity */
  entityType: string;
  /** The display name of this entity */
  displayName: string;
  /** Primary artist of this entity (metadata) */
  primaryArtist?: {
    /** The display name the primary artist */
    displayName: string;
    /** Id of the primary artist */
    id: string;
    /** Entity type of primary artist */
    type: string;
  };
  /** URL to a thumbnail image */
  thumbnailUrl?: string;
  /** Date of release of this entity */
  releaseDate?: number;
  /** Does this entity item represent a group entity */
  isGroup?: boolean;
  /** Number of children of this group (if group) */
  groupChildCount?: number;
  /** Additional metadata of this entity */
  entityData?: any;
  /** Number of releases related to this entity */
  releaseCount?: number;
  /** Number of artists related to this entity */
  artistCount?: number;
  /** Number of tracks related to this entity */
  trackCount?: number;
  /**
   * Optional boolean to determine if the entityItem should be a link
   * It will generate the proper url itself.
   */
  isLink?: boolean;
  /** If we have a link, should we keep the search parameters when navigating? */
  preserveSearch?: boolean;
  /** Required by React-emotion when composing styled components */
  className?: string;
  /** Should the Artwork show a gray background when loading or empty? */
  artworkHasBackground?: boolean;
  /** Is this the current active version you're looking at? */
  isHighlighted?: boolean;
  /** Enabling this will show more information in the metadata */
  showDetailedMetadata?: boolean;
  topic?: any[];
  /** current sorting value for browse opus */
  sortBy?: any;
  /** Name of table, for instance "Browse Opus" */
  tableName?: string;
  /** If the entity info block should be shown */
  isDisplayed?: boolean;
  releaseType?: string | null;
  releaseTypeColor?: string;
  isrc_list?: string[];
  withArtwork?: boolean;
  releaseCompilation?: string;
  isTrackCountDisplayed?: boolean;
  minWidth?: boolean;
}

interface IEntityItemIntProps extends IEntityItemExtProps {
  browseEntityType: EntityTypes;
}

/**
 * This component shows an Artwork entity name and metadata.
 * The EntityItem can be used as link or as static item.
 */
const EntityItem: React.FC<IEntityItemIntProps> = ({
  entityId,
  parentEntityId,
  entityType,
  browseEntityType,
  displayName,
  primaryArtist,
  thumbnailUrl,
  releaseDate,
  isGroup,
  groupChildCount,
  entityData,
  releaseCount,
  artistCount,
  trackCount,
  isLink = false,
  preserveSearch,
  className,
  artworkHasBackground = true,
  isHighlighted,
  showDetailedMetadata = false,
  topic,
  sortBy,
  tableName,
  isDisplayed,
  releaseType,
  releaseTypeColor,
  isrc_list,
  withArtwork = true,
  releaseCompilation,
  isTrackCountDisplayed,
  minWidth,
}) => {
  const Wrapper = isLink && !isHighlighted ? EntityLink : Entity;

  const url = getEntityUrl({
    entityId,
    entityType: browseEntityType,
    parentEntityId,
  });

  const isrcTooltipContent =
    isrc_list && isrc_list.length ? `ISRC:\n${isrc_list.join('\n')}` : null;

  return (
    <Wrapper
      className={className}
      to={isLink ? url : null}
      preserveSearch={preserveSearch}
      minWidth={minWidth}
    >
      {withArtwork && (
        <ArtworkWrapper>
          <StyledArtwork
            entityId={entityId}
            entityType={browseEntityType}
            title={displayName}
            src={thumbnailUrl}
            isVersion={Boolean(parentEntityId)}
            hasBackground={artworkHasBackground}
          />
        </ArtworkWrapper>
      )}
      <EntityMetadata>
        <TitleWrapper>
          <EntityTitle>
            {displayName}
            {releaseType && !releaseCompilation && (
              <ReleaseTypeWrapper releaseTypeColor={releaseTypeColor}>
                {releaseType}
              </ReleaseTypeWrapper>
            )}
            {releaseCompilation && (
              <ReleaseTypeWrapper releaseTypeColor={releaseTypeColor}>
                {releaseCompilation}
              </ReleaseTypeWrapper>
            )}
          </EntityTitle>
          {isHighlighted && <Tag>Currently viewing</Tag>}
        </TitleWrapper>
        <Metadata
          entityId={entityId}
          entityType={entityType}
          primaryArtist={primaryArtist && primaryArtist.displayName}
          releaseDate={releaseDate}
          isGroup={isGroup}
          isrcTooltipContent={isrcTooltipContent}
          isrc_list={isrc_list}
          groupChildCount={groupChildCount}
          metaData={entityData}
          releaseCount={releaseCount}
          artistCount={artistCount}
          trackCount={trackCount}
          showDetailedMetadata={showDetailedMetadata}
          topic={topic}
          isTrackCountDisplayed={isTrackCountDisplayed}
        />
        {isDisplayed && (
          <DoubleMetricCell
            sortBy={sortBy}
            header={sortBy}
            browseEntityType={browseEntityType}
            entityData={entityData}
            tableName={tableName}
            isInTable
          />
        )}
      </EntityMetadata>
    </Wrapper>
  );
};

export default withProps((props: IEntityItemExtProps) => ({
  browseEntityType: apiTypeToEntityType(props.entityType),
}))(EntityItem);
